export type TimeRangeMode = 'monthly' | 'quarterly' | 'annually' | 'custom';
export type Quarter = 1 | 2 | 3 | 4;

export interface TimeRange {
  mode: TimeRangeMode;
  startMonth: string; // "YYYY-MM"
  endMonth: string;   // "YYYY-MM"
}

export function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function createMonthlyRange(month: string): TimeRange {
  return { mode: 'monthly', startMonth: month, endMonth: month };
}

export function getQuarterRange(year: number, quarter: Quarter): { startMonth: string; endMonth: string } {
  const startM = (quarter - 1) * 3 + 1;
  const endM = quarter * 3;
  return {
    startMonth: `${year}-${String(startM).padStart(2, '0')}`,
    endMonth: `${year}-${String(endM).padStart(2, '0')}`,
  };
}

export function createQuarterlyRange(year: number, quarter: Quarter): TimeRange {
  const { startMonth, endMonth } = getQuarterRange(year, quarter);
  return { mode: 'quarterly', startMonth, endMonth };
}

export function getAnnualRange(year: number): { startMonth: string; endMonth: string } {
  return {
    startMonth: `${year}-01`,
    endMonth: `${year}-12`,
  };
}

export function createAnnualRange(year: number): TimeRange {
  const { startMonth, endMonth } = getAnnualRange(year);
  return { mode: 'annually', startMonth, endMonth };
}

export function createCustomRange(startMonth: string, endMonth: string): TimeRange {
  return { mode: 'custom', startMonth, endMonth };
}

export function getDefaultTimeRange(): TimeRange {
  return createMonthlyRange(getCurrentMonth());
}

export function isSingleMonth(range: TimeRange): boolean {
  return range.startMonth === range.endMonth;
}

/** Get the quarter (1-4) for a given "YYYY-MM" string */
export function monthToQuarter(month: string): Quarter {
  const m = parseInt(month.split('-')[1]);
  return Math.ceil(m / 3) as Quarter;
}

/** Get the year from a "YYYY-MM" string */
export function monthToYear(month: string): number {
  return parseInt(month.split('-')[0]);
}

export function formatTimeRangeLabel(range: TimeRange, locale: string): string {
  const fmt = (ym: string) => {
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  if (isSingleMonth(range)) return fmt(range.startMonth);

  const fmtShort = (ym: string) => {
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  };

  return `${fmtShort(range.startMonth)} - ${fmtShort(range.endMonth)}`;
}

/**
 * Convert a TimeRange to API query params.
 * Single month returns { month } for backward compat.
 * Multi-month returns { startMonth, endMonth }.
 */
export function timeRangeToApiParams(range: TimeRange): { month?: string; startMonth?: string; endMonth?: string } {
  if (isSingleMonth(range)) {
    return { month: range.startMonth };
  }
  return { startMonth: range.startMonth, endMonth: range.endMonth };
}
